/**
 * Where a customer is, and what order to visit them in.
 *
 * Shared so the map screen and the pending lists agree — the greedy sort used to
 * live only in ConnectMap, and a second copy would have drifted the moment one
 * of them learned about a new coordinate source.
 */

/** The coordinate fields every visit row carries, from findAllPending's join. */
export interface Placeable {
    /** Anchored by a real check-in. Empty until someone has actually been there. */
    customer_lat?: string | number | null;
    customer_lng?: string | number | null;
    /** Pasted in, or derived from the address. Orders the route; never the geofence. */
    geo_lat?: string | number | null;
    geo_lng?: string | number | null;
}

export interface Placed {
    lat: number;
    lng: number;
    /** True when this came from an address rather than someone standing there. */
    approx: boolean;
}

const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

/**
 * Where to put this customer on the map.
 *
 * A position recorded by an actual check-in beats one derived from an address,
 * so it wins when both exist. `approx` marks the address-derived case, which is
 * good enough to order a route but can be a street or a locality out.
 */
export function coordsOf(v: Placeable): Placed | null {
    const exactLat = num(v.customer_lat);
    const exactLng = num(v.customer_lng);
    if (exactLat !== null && exactLng !== null) return { lat: exactLat, lng: exactLng, approx: false };

    const guessLat = num(v.geo_lat);
    const guessLng = num(v.geo_lng);
    if (guessLat !== null && guessLng !== null) return { lat: guessLat, lng: guessLng, approx: true };

    return null;
}

/** Great-circle distance in kilometres. */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Order stops nearest-first, each hop measured from the stop before it.
 *
 * A greedy nearest-neighbour walk: not the shortest possible tour, but it needs
 * no round-trip to a routing service and gets most of the saving. Anything that
 * cannot be placed keeps its original order and goes last — it cannot be
 * positioned, so it cannot be sequenced, and leaving it mid-route would imply a
 * confidence the data does not support.
 *
 * `from` is where the rep is standing. Without it there is no first hop to
 * measure, so stops fall back to north-to-south bands, which still groups a
 * locality together.
 */
export function sortByRoute<T extends Placeable>(items: T[], from: { lat: number; lng: number } | null): T[] {
    const placeable = items.filter(i => coordsOf(i) !== null);
    const unplaceable = items.filter(i => coordsOf(i) === null);

    if (!from) {
        const banded = [...placeable].sort((a, b) => {
            const pa = coordsOf(a)!, pb = coordsOf(b)!;
            return pa.lat - pb.lat || pa.lng - pb.lng;
        });
        return [...banded, ...unplaceable];
    }

    const remaining = [...placeable];
    const ordered: T[] = [];
    let cursor = from;

    while (remaining.length > 0) {
        let nearestIdx = -1;
        let best = Infinity;
        for (let i = 0; i < remaining.length; i++) {
            const at = coordsOf(remaining[i])!;
            const d = distanceKm(cursor.lat, cursor.lng, at.lat, at.lng);
            if (d < best) { best = d; nearestIdx = i; }
        }
        if (nearestIdx === -1) break;
        const next = remaining[nearestIdx];
        ordered.push(next);
        cursor = coordsOf(next)!;
        remaining.splice(nearestIdx, 1);
    }

    return [...ordered, ...unplaceable];
}
